'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import NetruPartner from '@/components/NetruPartner';

const frameworks = [
  {
    tag: 'SOC 2',
    title: 'SOC 2 Type I & II',
    description:
      'Full readiness programme from gap analysis through to certification. We implement the technical controls, collect continuous evidence, and prepare your team for auditor interviews. Type I in weeks, Type II with continuous coverage.',
    controls: ['CC6 Logical Access', 'CC7 System Operations', 'CC8 Change Management', 'A1 Availability', 'C1 Confidentiality'],
  },
  {
    tag: 'ISO 27001',
    title: 'ISO 27001:2022',
    description:
      'ISMS design, risk treatment, policy management, and internal audit support. We build the management system that auditors expect, and that actually functions as a security programme, not just a document library.',
    controls: ['Annex A controls implementation', 'Risk register and treatment plan', 'ISMS documentation suite', 'Internal audit programme'],
  },
  {
    tag: 'DORA',
    title: 'DORA (Digital Operational Resilience Act)',
    description:
      'ICT risk management, incident classification and reporting, third-party oversight, and TLPT preparation for financial entities. Compliance by January 2025 deadline, and maintained continuously thereafter.',
    controls: ['ICT risk management framework', 'Incident response and reporting', 'Third-party risk management', 'TLPT coordination'],
  },
  {
    tag: 'GDPR',
    title: 'GDPR Technical Controls',
    description:
      'Data protection by design and by default, privacy impact assessments, data mapping, and the technical controls that underpin your GDPR programme. Engineering-led, not just legal documentation.',
    controls: ['Data protection by design', 'DPIA process and tooling', 'Data mapping and inventory', 'Breach detection and response'],
  },
];

const outcomes = [
  { metric: '< 12wk', label: 'SOC 2 Type I readiness timeline' },
  { metric: '365', label: 'Days of continuous evidence collection' },
  { metric: '0', label: 'Audit surprises, controls verified continuously' },
  { metric: '2x', label: 'Faster re-certification with maintained controls' },
];

const differentiators = [
  {
    title: 'Engineering-led, not documentation-led',
    body: 'Most compliance consultancies deliver policy documents. We implement the technical controls, encryption, access management, logging, monitoring, that make those policies real.',
  },
  {
    title: 'Continuous, not pre-audit scramble',
    body: 'Evidence is collected daily. Controls are monitored continuously. When your audit window opens, you are already ready, not spending three months gathering screenshots.',
  },
  {
    title: 'Compliance that stays compliant',
    body: 'We remain embedded after certification. Drift is detected and remediated before it becomes an audit finding. Your posture does not lapse between windows.',
  },
];

const PARTNER_HEADING = 'Walk into the audit already holding the evidence';
const PARTNER_BODY =
  'We build controls that produce their own evidence, so audit prep stops being a three-week scramble through screenshots. When you want a certificate on the end of it, Netru runs that programme independently.';

export default function CompliancePage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', right: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '350px', height: '350px', bottom: '0', left: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Compliance</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Compliance & Continuous Compliance]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Compliant on day one.<br />
              <span className="text-[#80edfa]">Compliant on day 365.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Most compliance engagements end when the certificate is issued. Ours do not. We build engineering-led programmes for SOC2, ISO 27001, DORA, and GDPR, implementing controls, collecting evidence continuously, and staying embedded to ensure your posture never lapses between audit windows.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Start your compliance programme</a>
              <a href="#frameworks" className="btn-secondary">Explore frameworks</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(2rem,4vw,3rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks */}
      <section id="frameworks" className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Frameworks covered</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Every major framework. One embedded team.
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                TechSonar engineers hold certifications across SOC2, ISO 27001, DORA, and GDPR. We do not subcontract compliance work, the same senior engineers who implement your controls support your audit.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {frameworks?.map((fw, i) => (
              <div key={i} className="border border-[#222] bg-[#181b1f] overflow-hidden fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className="h-1 w-full bg-gradient-to-r from-[#80edfa] to-transparent" />
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-2">{fw?.tag}</p>
                      <h3 className="font-heading text-xl text-[#f5f5f8] font-medium">{fw?.title}</h3>
                    </div>
                  </div>
                  <p className="text-sm text-[#aaa] leading-relaxed mb-6">{fw?.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {fw?.controls?.map((c, j) => (
                      <span key={j} className="text-xs text-[#666] border border-[#333] px-3 py-1 font-mono-brand">{c}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Continuous Compliance Detail */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-16 items-start">
            <div className="lg:w-1/2 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Continuous compliance</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight mb-6">
                Evidence collected daily. Not the week before your audit.
              </h2>
              <p className="text-[#aaa] text-base leading-relaxed mb-8">
                Traditional compliance is a point-in-time exercise. Controls are implemented for the audit, then drift. Evidence is gathered in a frantic three-month sprint before the auditor arrives. TechSonar builds compliance programmes that run continuously, automated evidence collection, drift detection, and control monitoring every day of the year.
              </p>
              <ul className="flex flex-col gap-4">
                {[
                  'Automated evidence collection mapped to control requirements',
                  'Continuous control monitoring with drift alerts',
                  'Policy management and version control',
                  'Risk register maintained and reviewed quarterly',
                  'Internal audit programme with independent assessment',
                  'Vendor and third-party risk management',
                ]?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <img src="https://cdn.prod.website-files.com/68a77090b95f85ab69076143/68fba43b700c01c8947096af_checkbox.svg" alt="" className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#aaa]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-1/2 flex flex-col gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
              {[
                { phase: 'Phase 1', title: 'Gap Analysis & Scoping', duration: 'Weeks 1–2', desc: 'Current state assessment against target framework. Control gaps identified, risk register seeded, programme roadmap agreed.' },
                { phase: 'Phase 2', title: 'Control Implementation', duration: 'Weeks 3–10', desc: 'Technical controls implemented by embedded engineers. Policies written, tooling configured, evidence collection automated.' },
                { phase: 'Phase 3', title: 'Audit Readiness', duration: 'Weeks 10–12', desc: 'Internal audit, evidence review, auditor liaison. Type I readiness confirmed. Continuous monitoring activated.' },
                { phase: 'Phase 4', title: 'Continuous Compliance', duration: 'Ongoing', desc: 'Daily evidence collection, monthly control reviews, quarterly risk assessments. Type II window maintained continuously.' },
              ]?.map((p, i) => (
                <div key={i} className="border border-[#222] bg-[#181b1f] p-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">{p?.phase}</p>
                    <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest">{p?.duration}</p>
                  </div>
                  <h4 className="font-heading text-base text-[#f5f5f8] font-medium mb-2">{p?.title}</h4>
                  <p className="text-xs text-[#666] leading-relaxed">{p?.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Why TechSonar</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              Compliance that does not lapse
            </h2>
          </div>
          <div className="flex flex-col lg:flex-row gap-px bg-[#222]">
            {differentiators?.map((d, i) => (
              <div key={i} className="flex-1 bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-6" />
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{d?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{d?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Ready to start your compliance programme?
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <NetruPartner

        heading={PARTNER_HEADING}

        body={PARTNER_BODY}

      />


      <Footer />
    </div>
  );
}
